// Consultant statements are built entirely on-device from synced visits and payouts.
// They deliberately carry no patient identifiers: no name, phone, code, diagnosis or remarks.

const num = (v) => Number(v) || 0;
const inRange = (d, from, to) => !!d && d.slice(0, 10) >= from && d.slice(0, 10) <= to;
const monthKey = (d) => d.slice(0, 7);

export const monthLabel = (ym) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });

export const dateLabel = (d) =>
  new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

export const monthRange = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
};

export function buildStatement(consultant, visits, settlements, from, to) {
  const mine = visits.filter((v) => v.consultant_id === consultant.id);
  const paidRows = settlements.filter((p) => p.consultant_id === consultant.id);

  const lines = mine.filter((v) => inRange(v.date, from, to))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .map((v) => ({ date: v.date.slice(0, 10), treatment: v.treatment || "—", fee: num(v.consultant_fee) }));
  const payments = paidRows.filter((p) => inRange(p.date, from, to))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
    .map((p) => ({ date: p.date.slice(0, 10), note: [p.method, p.note].filter(Boolean).join(" · "), amount: num(p.amount) }));

  // Balance carried in from before the period, so the closing pending figure matches the running ledger.
  const earnedBefore = mine.filter((v) => v.date && v.date.slice(0, 10) < from).reduce((a, v) => a + num(v.consultant_fee), 0);
  const paidBefore = paidRows.filter((p) => p.date && p.date.slice(0, 10) < from).reduce((a, p) => a + num(p.amount), 0);
  const opening = earnedBefore - paidBefore;

  const byTreatment = {};
  for (const l of lines) {
    const t = (byTreatment[l.treatment] ||= { treatment: l.treatment, cases: 0, fee: 0 });
    t.cases += 1; t.fee += l.fee;
  }

  const byMonth = {};
  const month = (k) => (byMonth[k] ||= { month: k, cases: 0, fee: 0, paid: 0 });
  for (const l of lines) { const m = month(monthKey(l.date)); m.cases += 1; m.fee += l.fee; }
  for (const p of payments) month(monthKey(p.date)).paid += p.amount;

  const cases = lines.length;
  const fee = lines.reduce((a, l) => a + l.fee, 0);
  const paid = payments.reduce((a, p) => a + p.amount, 0);
  return {
    consultant, from, to, lines, payments,
    treatments: Object.values(byTreatment).sort((a, b) => b.fee - a.fee),
    months: Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month)),
    totals: { cases, fee, paid, opening, pending: opening + fee - paid },
  };
}

const rs = (n) => "₹" + Math.round(n).toLocaleString("en-IN");
const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
const safeColor = (c, fallback) => (/^#[0-9a-f]{6}$/i.test(c || "") ? c : fallback);

// Images are inlined as data URLs so the file is self-contained (and never carries an auth token).
async function imageData(src) {
  if (!src) return null;
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const table = (title, head, rows, { foot, left = 1, empty } = {}) => {
  const cls = (i) => (i < left ? "" : ' class="n"');
  const body = rows.length
    ? rows.map((r) => `<tr>${r.map((c, i) => `<td${cls(i)}>${esc(c)}</td>`).join("")}</tr>`).join("")
    : `<tr><td class="empty" colspan="${head.length}">${esc(empty)}</td></tr>`;
  return `<section><h2>${esc(title)}</h2><div class="scroll"><table>
    <thead><tr>${head.map((h, i) => `<th${cls(i)}>${esc(h)}</th>`).join("")}</tr></thead>
    <tbody>${body}</tbody>
    ${foot && rows.length ? `<tfoot><tr>${foot.map((c, i) => `<td${cls(i)}>${esc(c)}</td>`).join("")}</tr></tfoot>` : ""}
  </table></div></section>`;
};

export async function statementHtml(st, clinic, clinicLogoSrc) {
  const { consultant: c, totals: t } = st;
  const accent = safeColor(clinic?.primary_color, "#2563EB");
  const accent2 = safeColor(clinic?.secondary_color, "#0EA5A4");
  const [dmLogo, clinicLogo] = await Promise.all([imageData("/dentmemo-logo-transparent.png"), imageData(clinicLogoSrc)]);
  const period = `${dateLabel(st.from)} – ${dateLabel(st.to)}`;
  const tiles = [
    ["Cases", t.cases], ["Consultant fee", rs(t.fee)], ["Opening balance", rs(t.opening)],
    ["Paid this period", rs(t.paid)], ["Pending", rs(t.pending)],
  ];

  const page = `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="robots" content="noindex">
<title>${esc(c.name)} · Statement · ${esc(period)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link href="https://fonts.googleapis.com/css2?family=Manrope:wght@300;600;800&family=IBM+Plex+Sans:wght@400;600&family=JetBrains+Mono:wght@500;600&display=swap" rel="stylesheet">
<style>
  :root { --a: ${accent}; --a2: ${accent2}; --ink: #0f172a; --mut: #64748b; --line: #e2e8f0; --soft: #f8fafc; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #eef2f7; color: #334155; font: 14px/1.5 "IBM Plex Sans", system-ui, -apple-system, "Segoe UI", sans-serif; -webkit-font-smoothing: antialiased; }
  .sheet { max-width: 860px; margin: 32px auto; background: #fff; border-radius: 20px; overflow: hidden; box-shadow: 0 1px 2px rgba(15,23,42,.06), 0 12px 40px rgba(15,23,42,.08); }
  .band { height: 6px; background: linear-gradient(90deg, var(--a), var(--a2)); }
  header { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; padding: 32px 40px 24px; border-bottom: 1px solid var(--line); flex-wrap: wrap; }
  .clinic { display: flex; gap: 14px; align-items: center; }
  .clinic img { width: 48px; height: 48px; border-radius: 12px; object-fit: contain; background: var(--soft); }
  .clinic .mark { width: 48px; height: 48px; border-radius: 12px; display: grid; place-items: center; background: var(--a); color: #fff; font: 800 20px "Manrope", sans-serif; }
  h1, h2, .name { font-family: "Manrope", system-ui, sans-serif; color: var(--ink); margin: 0; }
  h1 { font-size: 20px; font-weight: 800; letter-spacing: -.01em; }
  .sub { color: var(--mut); font-size: 13px; }
  .kicker { font: 600 11px "Manrope", sans-serif; letter-spacing: .14em; text-transform: uppercase; color: var(--a); text-align: right; }
  .period { font: 600 15px "Manrope", sans-serif; color: var(--ink); text-align: right; margin-top: 4px; }
  .who { padding: 24px 40px 4px; }
  .name { font-size: 26px; font-weight: 300; letter-spacing: -.02em; }
  .name b { font-weight: 800; }
  .tiles { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; padding: 20px 40px 8px; }
  .tile { border: 1px solid var(--line); border-radius: 14px; padding: 14px 16px; background: var(--soft); }
  .tile .l { font: 600 10.5px "Manrope", sans-serif; letter-spacing: .1em; text-transform: uppercase; color: var(--mut); }
  .tile .v { font: 600 19px "JetBrains Mono", ui-monospace, monospace; color: var(--ink); margin-top: 4px; }
  .tile.hi { background: var(--a); border-color: var(--a); }
  .tile.hi .l { color: rgba(255,255,255,.8); } .tile.hi .v { color: #fff; }
  section { padding: 22px 40px 0; }
  h2 { font-size: 15px; font-weight: 700; margin-bottom: 10px; }
  .scroll { overflow-x: auto; border: 1px solid var(--line); border-radius: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th { background: var(--soft); color: var(--mut); font-weight: 600; font-size: 11.5px; text-transform: uppercase; letter-spacing: .05em; text-align: left; padding: 10px 14px; border-bottom: 1px solid var(--line); }
  td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; white-space: nowrap; }
  tbody tr:last-child td { border-bottom: 0; }
  .n { text-align: right; font-family: "JetBrains Mono", ui-monospace, monospace; color: var(--ink); }
  tfoot td { border-top: 1px solid #cbd5e1; border-bottom: 0; font-weight: 700; color: var(--ink); background: #fcfdfe; }
  .empty { color: var(--mut); text-align: center; padding: 18px; }
  .note { margin: 22px 40px 0; padding: 12px 16px; border-radius: 12px; background: var(--soft); color: var(--mut); font-size: 12.5px; }
  footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 28px; padding: 18px 40px; border-top: 1px solid var(--line); color: #94a3b8; font-size: 12px; }
  footer .dm { display: flex; align-items: center; gap: 8px; color: var(--mut); text-decoration: none; }
  footer .dm img { height: 18px; }
  footer .dm b { font-family: "Manrope", sans-serif; color: var(--ink); }
  @media (max-width: 640px) {
    .sheet { margin: 0; border-radius: 0; }
    header, .who, section, footer { padding-left: 18px; padding-right: 18px; }
    .tiles { grid-template-columns: repeat(2, 1fr); padding: 16px 18px 4px; }
    .tile.hi { grid-column: span 2; }
    .note { margin: 18px 18px 0; }
    .scroll { border: 0; border-radius: 0; margin: 0 -18px; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
    table { font-size: 12px; }
    th { font-size: 10px; letter-spacing: .03em; padding: 8px 6px; white-space: normal; }
    td { padding: 9px 6px; white-space: normal; }
    th:first-child, td:first-child { padding-left: 18px; } th:last-child, td:last-child { padding-right: 18px; }
    .n { white-space: nowrap; }
    .kicker, .period { text-align: left; }
  }
  @media print {
    body { background: #fff; } .sheet { margin: 0; box-shadow: none; border-radius: 0; max-width: none; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; } section { break-inside: avoid; }
  }
</style></head>
<body><main class="sheet">
  <div class="band"></div>
  <header>
    <div class="clinic">
      ${clinicLogo ? `<img src="${clinicLogo}" alt="">` : `<div class="mark">${esc((clinic?.name || "C").trim()[0] || "C")}</div>`}
      <div><h1>${esc(clinic?.name || "Clinic")}</h1>${clinic?.dentist_name ? `<div class="sub">${esc(clinic.dentist_name)}</div>` : ""}</div>
    </div>
    <div><div class="kicker">Consultant statement</div><div class="period">${esc(period)}</div></div>
  </header>
  <div class="who">
    <div class="name"><b>${esc(c.name)}</b></div>
    <div class="sub">${esc([c.specialty, c.phone].filter(Boolean).join(" · ") || "Visiting consultant")}</div>
  </div>
  <div class="tiles">${tiles.map(([l, v], i) => `<div class="tile${i === tiles.length - 1 ? " hi" : ""}"><div class="l">${esc(l)}</div><div class="v">${esc(v)}</div></div>`).join("")}</div>
  ${st.months.length > 1 ? table("Month-wise totals", ["Month", "Cases", "Consultant fee", "Paid"],
    st.months.map((m) => [monthLabel(m.month), m.cases, rs(m.fee), rs(m.paid)]),
    { foot: ["Total", t.cases, rs(t.fee), rs(t.paid)] }) : ""}
  ${table("By treatment", ["Treatment", "Cases", "Consultant fee"],
    st.treatments.map((r) => [r.treatment, r.cases, rs(r.fee)]),
    { foot: ["Total", t.cases, rs(t.fee)], empty: "No cases in this period." })}
  ${table("Cases", ["#", "Date", "Treatment", "Consultant fee"],
    st.lines.map((l, i) => [i + 1, dateLabel(l.date), l.treatment, rs(l.fee)]),
    { left: 3, foot: ["", "", "Total", rs(t.fee)], empty: "No cases in this period." })}
  ${table("Payments", ["Date", "Note", "Amount"],
    st.payments.map((p) => [dateLabel(p.date), p.note || "—", rs(p.amount)]),
    { left: 2, foot: ["Total paid", "", rs(t.paid)], empty: "No payments in this period." })}
  <div class="note">Pending = opening balance + consultant fee − paid this period. Patient details are not included in this statement.</div>
  <footer>
    <a class="dm" href="https://dentmemo.in" target="_blank" rel="noopener">${dmLogo ? `<img src="${dmLogo}" alt="">` : ""}<span>Generated using <b>DentMemo</b></span></a>
    <span>Generated ${esc(dateLabel(new Date().toISOString()))}</span>
  </footer>
</main></body></html>`;

  const slug = (v) => v.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
  return new File([page], `${slug(c.name)}-statement-${st.from}-to-${st.to}.html`, { type: "text/html" });
}

export function statementText(st, clinic) {
  const r = rs;
  const t = st.totals;
  return [
    `${clinic?.name || "Clinic"} — consultant statement`,
    `${st.consultant.name}, ${dateLabel(st.from)} – ${dateLabel(st.to)}`,
    "",
    `Cases: ${t.cases}`,
    `Your consultant fee: ${r(t.fee)}`,
    `Opening balance: ${r(t.opening)}`,
    `Paid this period: ${r(t.paid)}`,
    `Pending: ${r(t.pending)}`,
    "",
    "Full statement attached — open it in any browser.",
    "Generated using DentMemo",
  ].join("\n");
}
